import {
  BranchQuoteSupport,
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  ResultConversation,
  SupportQuoteItemLinkDetail,
  ViewConversationEntry
} from '../../api/dealer-api-interface-franchisee';
import { cache } from '../cache-impl/cache-registry';
import { ConversationActionTemplate } from './conversation-types';
import { ConversationEntryUpdateEvent, CreateConversationEntry } from './wm-conversation-entry';
import { customElement, property, state } from 'lit/decorators.js';
import { flagInSet, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { getApiFactory } from '../../api/api-injector';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { getQuoteNumberFormatted } from '../../quotes/data/quote-helper-functions';
import { getSupportStatusBadge } from './conversation-helper';
import { html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { lockUIandExecute } from '../../../webmodule-common/other/ui-lock';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { repeat } from 'lit/directives/repeat.js';
import { showValidations } from '../../../webmodule-common/other/ui/modal-validationhandler';
import { LangBranchQuoteConversation } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-conversation-lang';
import { WebmoduleInputEvent } from '../../../webmodule-common/components/src/events/webmodule-input';
import { WebmoduleChangeEvent } from '../../../webmodule-common/components/src/events/webmodule-change';
import { FormInputAssistantBase } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant-base';
import { LangBranchQuoteSupportNew } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import { FranchiseeQuoteItemSelectorTable, QuoteItemSelectorModal } from '../quotes/views/franchisee-quote-item-table';

import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';

export interface ConversationActions {
  attachmentsOnlySwitch: ConversationActionTemplate;
  cancel: ConversationActionTemplate;
  reactivate: ConversationActionTemplate;
  resolve: ConversationActionTemplate;
  postToSupplier: ConversationActionTemplate;
  close: ConversationActionTemplate;
  addNote: ConversationActionTemplate;
}

@customElement('wm-branchquoteconversationview')
export class BranchQuoteConversationView extends LitElementBase {
  actions?: ConversationActions;
  @property() supplierName = '';
  @property() initialDisplayLead = 1;
  @property() initialDisplayTail = 3;
  @property() showSubject = true;
  @property() showQuoteItemLink = false;
  @property() isDataReadonly = false;
  @property() showButtons = true;
  @property() showHeader = true;
  @property() attachmentsMode = false;
  @property() concurrency = '';
  @property({ type: Boolean }) sortAscending = false;
  @property()
  emptyNote = LangBranchQuoteConversation.noEntriesVisible();
  @state() displayCount = 4;
  @state() public quoteTitle?: TemplateResult;
  protected subjectEditorValue?: string;
  private _firstUpdated = false;
  private _resultsLoaded = false;
  private _users: string[] = [];

  @state()
  private _results: ResultConversation | null = null;

  public get results(): ResultConversation | null {
    return this._results;
  }

  public set results(value: ResultConversation | null) {
    this._results = value;
    this._resultsLoaded = value !== null;
    this.activateEditors();
  }

  @property({ type: Boolean })
  private _alwaysShowNewNoteEditor = false;

  public get alwaysShowNewNoteEditor() {
    return this._alwaysShowNewNoteEditor;
  }

  public set alwaysShowNewNoteEditor(value) {
    this._alwaysShowNewNoteEditor = value;

    if (value) {
      if (this.useExternalEditing) {
        this.ActivateExternalEditor();
      } else this.showNewNoteEditor = true;
    } else {
      if (this.useExternalEditing) {
        this.createEntryEditor = undefined;
        this.setEditEntryEditor(undefined, undefined);
      }
    }
  }

  @property()
  private _showNewNoteEditor = false;

  public get showNewNoteEditor() {
    return this._showNewNoteEditor;
  }

  public set showNewNoteEditor(value) {
    if (value !== this._showNewNoteEditor) {
      this._showNewNoteEditor = value;
      if (this._showNewNoteEditor) {
        this.buildCreateEntryEditor();
      } else this.createEntryEditor = undefined;
    }
  }

  @property()
  private _useExternalEditing = false;

  public get useExternalEditing() {
    return this._useExternalEditing;
  }

  public set useExternalEditing(value) {
    this._useExternalEditing = value;
    if (value && this.alwaysShowNewNoteEditor) {
      this.ActivateExternalEditor();
    }
  }
  @property() quoteManager: FranchiseeQuoteContainerManager;
  @property()
  private _branchQuoteSupport?: BranchQuoteSupport | undefined;

  public get branchQuoteSupport(): BranchQuoteSupport | undefined {
    return this._branchQuoteSupport;
  }

  public set branchQuoteSupport(value: BranchQuoteSupport | undefined) {
    if (this._branchQuoteSupport !== value || this.concurrency !== value?.recordVersion) {
      this.subjectEditorValue = value?.subject;
      this.concurrency = value?.recordVersion ?? '';
      this.results = null;
      this._branchQuoteSupport = value;
      this.setEditEntryEditor(undefined, undefined);
      this.showNewNoteEditor = false;
      this.loadConversation(); // async delayed
      this.linkedQuoteItemKeys = this.getQuoteItemLinksAsArray();
    }
  }

  getQuoteItemLinksAsArray() {
    if (!this.branchQuoteSupport) return [];
    return Object.entries(this._branchQuoteSupport?.quoteItemLink ?? {})
      .filter(([, v]) => v.isLinked)
      .map(([k]) => k);
  }

  @state()
  private _createEntryEditor?: TemplateResult | undefined;

  public get createEntryEditor(): TemplateResult | undefined {
    return this._createEntryEditor;
  }

  public set createEntryEditor(value: TemplateResult | undefined) {
    this._createEntryEditor = value;
    this.notifyCreateEntryEditor();
    this.requestUpdate();
  }

  @state()
  private _editEntryEditor?: TemplateResult | undefined;

  public get editEntryEditor(): TemplateResult | undefined {
    return this._editEntryEditor;
  }

  private _editEntry: ViewConversationEntry | undefined;

  public get editEntry(): ViewConversationEntry | undefined {
    return this._editEntry;
  }

  public get hasChanged(): boolean {
    if (!this.branchQuoteSupport) return false;
    if (this.showSubject && this.subjectEditorValue !== this.branchQuoteSupport.subject) return true;
    if (this.showQuoteItemLink) {
      const backupLinks = this.getQuoteItemLinksAsArray();
      if (backupLinks.length !== this.linkedQuoteItemKeys.length) return true;
      if (JSON.stringify(backupLinks.sort()) !== JSON.stringify(this.linkedQuoteItemKeys.sort())) return true;
    }
    return false;
  }

  get showEditor() {
    return !this.useExternalEditing && this.showNewNoteEditor;
  }

  protected get canReactivate() {
    return (
      !this.isDataReadonly &&
      this.branchQuoteSupport &&
      // eslint-disable-next-line no-bitwise
      flagInSet(this.branchQuoteSupport.status, BranchQuoteSupportStatus.Resolved | BranchQuoteSupportStatus.Cancelled)
    );
  }

  protected get isReadonly() {
    return (
      this.readonly ||
      this.attributeTrue('readonly') ||
      !this.branchQuoteSupport ||
      // eslint-disable-next-line no-bitwise
      flagInSet(this.branchQuoteSupport.status, BranchQuoteSupportStatus.Resolved | BranchQuoteSupportStatus.Cancelled)
    );
  }

  ActivateExternalEditor() {
    const currentUserId = getCurrentUser()?.id;
    const draftentry = this.results?.conversationEntries.find(x => x.systemUserId === currentUserId && x.draft);
    if (!draftentry) {
      if (this.alwaysShowNewNoteEditor) this.showNewNoteEditor = true;
    } else this.editConversationEntry(draftentry);
  }

  //TODO: Pass users in
  buildCreateEntryEditor() {
    this.setEditEntryEditor(undefined, undefined);
    this.createEntryEditor = !this._showNewNoteEditor
      ? undefined
      : html` <wm-webmoduleconversationentrycreate
          .conversationEntryId=${newGuid()}
          .conversationId=${this.branchQuoteSupport?.id}
          id="addNoteEditor"
          .allowCancel=${!this.useExternalEditing}
          .users=${this._users}
          @wm-event-create=${this.eventCreateEntry}
          @wm-event-cancel=${this.eventCancelNewEntry}
        ></wm-webmoduleconversationentrycreate>`;
  }

  public eventDeleteEntry = async (e: CustomEvent<ConversationEntryUpdateEvent>) => {
    if (this.results)
      this.results.conversationEntries = this.results.conversationEntries.filter(
        x => x.id !== e.detail.input.conversationEntryId
      );
    this.activateEditors();
    this.requestUpdate();
  };

  public eventExternalEditConversation = async (e: CustomEvent<{ entry: ViewConversationEntry }>) => {
    if (this.showNewNoteEditor && this.createEntryEditor) {
      this.showNewNoteEditor = false;
    }
    this.editConversationEntry(e.detail.entry);
  };

  public setEditEntryEditor(value: TemplateResult | undefined, entry?: ViewConversationEntry) {
    this._editEntryEditor = value;
    this._editEntry = entry;
    this.notifyEditEntryEditor();
    this.requestUpdate();
  }

  public eventCancelNewEntry = async (e: CustomEvent) => {
    e.stopImmediatePropagation();
    this.activateEditors();
  };

  public eventCreateEntry = async (e: CustomEvent<CreateConversationEntry>) => {
    const data = e.detail;
    const attachments = data.attachments;
    const users = data.users;
    const text = data.text ?? '';
    await lockUIandExecute(async () => {
      if (!this.branchQuoteSupport) return;
      const branchQuoteSupportId = isEmptyOrSpace(this.branchQuoteSupport.recordVersion)
        ? null
        : this.branchQuoteSupport.id;
      const result = await getApiFactory().franchisee().addConversationEntry({
        conversationEntryId: data.id,
        branchQuoteSupportId: branchQuoteSupportId,
        conversationId: this.branchQuoteSupport.id,
        text: text,
        draft: data.draft,
        users: users,
        attachments: attachments
      });
      if (result) {
        this.results?.conversationEntries.push(result.conversationEntry);

        const eventResult = this.dispatchCustom('conversation-created', {
          updateEvent: e.detail,
          showMessage: true,
          completionPromise: null
        });

        await eventResult.completionPromise;

        if (data.draft && eventResult.showMessage) await this.showDraftSavedMessage();
        if (data.draft && result && this.useExternalEditing) {
          this.editConversationEntry(result.conversationEntry);
        }
        this.activateEditors();
      }
    });
    this.requestUpdate();
  };

  public getValidationErrors(): string[] {
    const subject = this.showSubject ? (this.subjectEditorValue ?? '') : (this.branchQuoteSupport?.subject ?? '');
    if (isEmptyOrSpace(subject)) {
      return [LangBranchQuoteConversation.supportSubjectMustHaveValue()];
    }
    return [];
  }

  public async save() {
    return await this.updateBranchQuoteSupport(
      this.showSubject ? (this.subjectEditorValue ?? '') : (this.branchQuoteSupport?.subject ?? '')
    );
  }

  public eventClose = async (_e: Event) => {
    if (this.hasChanged) {
      const validations = this.getValidationErrors();
      if (validations.length > 0) {
        await showValidations(validations);
        return;
      }
      await this.save();
    }
    this.dispatchCustom('close', {});
  };

  public eventCancel = async (_e: Event) => {
    await this.changeState(BranchQuoteSupportStatus.Cancelled);
  };

  public eventResolve = async (_e: Event) => {
    await this.changeState(BranchQuoteSupportStatus.Resolved);
  };

  public eventReactivate = async (_e: Event) => {
    await this.changeState(BranchQuoteSupportStatus.ReActivated);
  };

  public eventActivate = async (_e: Event) => {
    await this.changeState(BranchQuoteSupportStatus.New);
  };

  async loadConversation() {
    if (!this.branchQuoteSupport) {
      this.results = null;
      return;
    }

    const results = await getApiFactory().franchisee().getConversation({
      conversationId: this.branchQuoteSupport.id,
      count: 0, //take all
      fetchAllAttachments: false,
      fetchAllUsers: false,
      maxRecordVersion: null,
      sortAscending: true,
      startIndex: 0
    });
    if (results) await cache().userProfile.preFetch(results?.conversationEntries.map(x => x.systemUserId));

    const userId = getCurrentUser()?.id;

    const userList = Array.from(
      new Set(results?.conversationEntries.filter(x => x.systemUserId !== userId).map(x => x.systemUserId))
    );

    this._users = userList ?? [];

    const quoteSummary = (await cache().quote.getData(this.branchQuoteSupport.branchQuoteId))?.quoteSummary;
    this.quoteTitle = html`<h6>
      ${LangBranchQuoteConversation.createdBy({ quoteNumber: getQuoteNumberFormatted(quoteSummary) })}
    </h6>`;
    this.results = results;
  }

  connectedCallback(): void {
    super.connectedCallback();
  }

  render() {
    const header = this.getFullHeaderTemplate(
      this.showSubject,
      this.showButtons,
      this.showHeader,
      this.showQuoteItemLink
    );

    return html` <div class="conversation">
      ${header}${this.conversationTemplates()} ${this.addNoteTemplate()} ${this.footerTemplate()}
    </div>`;
  }

  public getFullHeaderTemplate(
    showSubject: boolean,
    showButtons: boolean,
    showHeader: boolean,
    showQuoteItemLink: boolean
  ) {
    const forms = new FormInputAssistantBase(() => this.readonly || this.isDataReadonly);
    const quoteTitle = this.showHeader && this.quoteTitle ? html` <div>${this.quoteTitle}</div>` : html``;
    const linkKeysTemplate =
      this.linkedQuoteItemKeys.length > 0
        ? html`<div class="quote-item-selector-container">
            <franchisee-quote-item-selector-table
              id="table"
              .readonly=${true}
              .allowOpenLink=${true}
              .quoteManager=${this.quoteManager}
              .showSelectedOnly=${true}
              .quoteItemKeys=${this.linkedQuoteItemKeys}
              @wm-component-ready=${(e: CustomEvent<FranchiseeQuoteItemSelectorTable>) => {
                const quoteItemLinkTable = e.detail;
                quoteItemLinkTable?.setSelectedItems(this.linkedQuoteItemKeys);
              }}
            ></franchisee-quote-item-selector-table>
          </div>`
        : nothing;
    const links = showQuoteItemLink
      ? html` <div class="d-flex conversation-link-wrapper">
            ${forms.multiEditRow({
              title: LangBranchQuoteSupportNew.linkQuoteItems(),
              toolTip: LangBranchQuoteSupportNew.linkQuoteItemsLabel(),
              slotted: html`<webmodule-button
                size="small"
                variant="primary"
                @click=${(e: Event) => this.showOrSelectLinkedItems(e)}
              >
                ${LangBranchQuoteSupportNew.updateLinkQuoteItems()}
              </webmodule-button>`
            })}
          </div>
          <div class="d-flex conversation-link-wrapper">${linkKeysTemplate}</div>`
      : nothing;
    const subject = showSubject
      ? html` <div class="d-flex conversation-subject-wrapper align-items-center">
          <webmodule-input
            id="subject"
            .readonly=${this.readonly || this.isDataReadonly}
            .disabled=${this.readonly || this.isDataReadonly}
            class="conversation-subject webmodule-control-left-label"
            type="text"
            label="${LangBranchQuoteConversation.supportSubject()}:"
            maxlength="100"
            size="small"
            @webmodule-change=${this.eventSubjectChanged}
            @webmodule-input=${this.eventSubjectChanged}
            value=${this.branchQuoteSupport?.subject ?? ''}
          ></webmodule-input>
        </div>`
      : html``;
    const subjectButtons = showButtons
      ? html` <div class="d-flex conversation-subject-actions ms-3">
          ${this.buttonsTemplates()}${getSupportStatusBadge(this.branchQuoteSupport?.status)}
        </div>`
      : html``;
    const header = showHeader
      ? html`${quoteTitle}
          <div class="conversation-header mb-3">${subject}${links} ${subjectButtons}</div>`
      : html``;
    return header;
  }

  @state()
  protected linkedQuoteItemKeys: string[] = [];

  makeDisplayValueForQuoteItemId(id: string) {
    return this.quoteManager?.makeSupportLinkDisplayValueForQuoteItemId(id) ?? '';
  }

  protected getQuoteItemLinks(): Record<string, SupportQuoteItemLinkDetail> {
    return this.linkedQuoteItemKeys.reduce<Record<string, SupportQuoteItemLinkDetail>>((acc, key) => {
      acc[key] = { isLinked: true, id: key, textDisplay: this.makeDisplayValueForQuoteItemId(key) };
      return acc;
    }, {});
  }

  async showOrSelectLinkedItems(e: Event) {
    if (!this.quoteManager) return;
    const modal = new QuoteItemSelectorModal(this.quoteManager!, this.linkedQuoteItemKeys);
    await modal.showModal();
    if (modal.result?.success) {
      this.linkedQuoteItemKeys = modal.result.keys;
      // Find the closest parent with class 'conversation-header'
      const header = (e.target as HTMLElement).closest('.conversation-header');
      if (header) {
        // Find the child element with id 'table'
        const table = header.querySelector<FranchiseeQuoteItemSelectorTable>('#table');
        if (table) {
          table.setSelectedItems(this.linkedQuoteItemKeys);
        }
      }
    }
  }

  footerTemplate(): unknown {
    const addNote =
      !this.isReadonly && !this.useExternalEditing
        ? html` <webmodule-button size="small" variant="primary" @click=${this.eventAddNote}
            >${LangBranchQuoteConversation.addNote()}
          </webmodule-button>`
        : html``;

    return !this.showNewNoteEditor && !this.useExternalEditing
      ? html` <div class="conversation-list-actions">${addNote}</div>`
      : html``;
  }

  addNoteTemplate(): unknown {
    return !this.useExternalEditing && this.showNewNoteEditor ? this.createEntryEditor : html``;
  }

  hasDownloads(entry: ViewConversationEntry) {
    return entry.attachments.some(x => !x.embedded);
  }

  conversationTemplates(): unknown {
    const userId = getCurrentUser()?.id;

    const data = this.results?.conversationEntries ?? [];
    const entries = this.attachmentsMode
      ? [
          ...data.filter(item => !item.draft && this.hasDownloads(item)),
          ...data.filter(item => item.draft && item.systemUserId === userId && this.hasDownloads(item))
        ]
      : [...data.filter(item => !item.draft), ...data.filter(item => item.draft && item.systemUserId === userId)];

    const sortDirection = this.sortAscending ? -1 : 1;
    entries.sort((a: ViewConversationEntry, b: ViewConversationEntry) => {
      if (a.dateCreated === b.dateCreated) return 0;

      const order = a.dateCreated < b.dateCreated ? -1 : 1;
      return order * sortDirection;
    });

    const results: any[] = [];
    const repeatRange = (start: number, end?: number) => {
      return repeat(
        entries.slice(start, end),
        entry => entry.id,
        entry => html`
          <div class="conversation-messages">
            <wmview-conversation-entry-view
              .inlineEditing=${!this.useExternalEditing}
              .attachmentsOnly=${this.attachmentsMode}
              .readonly=${this.isReadonly}
              @wm-event-delete=${this.eventDeleteEntry}
              @wm-event-request-edit=${this.eventExternalEditConversation}
              .conversationEntry=${entry}
            ></wmview-conversation-entry-view>
          </div>
        `
      );
    };

    if (entries.length === 0 && !this.showEditor) return html`<h1 class="text-muted">${this.emptyNote}</h1>`;
    if (
      this.attachmentsMode ||
      this.initialDisplayLead + this.initialDisplayTail >= entries.length ||
      this.displayCount >= entries.length
    )
      return repeatRange(0);
    const leading = this.displayCount - this.initialDisplayTail;
    results.push(repeatRange(0, leading));
    if (this.displayCount < entries.length) {
      results.push(this.showMore(entries.length));
      results.push(repeatRange(entries.length - this.initialDisplayTail));
    }
    return results;
  }

  showMore(count: number): any {
    const showSize = 3;
    const available = count - this.displayCount;
    const showMore = Math.min(showSize, available);
    const eventShowMore = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.displayCount = Math.min(count, this.displayCount + showMore);
    };
    const eventShowAll = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.displayCount = count;
    };
    if (showMore <= 0) return html``;

    const showAll =
      available > showSize
        ? html`<a class="ms-5" href="#" @click=${eventShowAll}
            >${LangBranchQuoteConversation.showAllMessages({ count })}</a
          >`
        : html``;
    return html`
      <div class="row">
        <div class="col d-flex justify-content-center border-top border-bottom border-secondary p-3">
          <a href="#" @click=${eventShowMore}>${LangBranchQuoteConversation.showMoreMessages({ count: showMore })}</a
          >${showAll}
        </div>
      </div>
    `;
  }

  actionsTemplate(): ConversationActions {
    const result: ConversationActions = {
      addNote: (hide?: boolean) => {
        const disabled = !(!this.isReadonly && !this.useExternalEditing);
        return disabled && hide
          ? html``
          : html` <webmodule-button size="small" variant="primary" ?disabled=${disabled} @click=${this.eventAddNote}>
              ${LangBranchQuoteConversation.addNote()}
            </webmodule-button>`;
      },
      attachmentsOnlySwitch: () => {
        const attachmentsTitle = LangBranchQuoteConversation.attachmentsOnly();

        return html` <wm-button-switch
          .checked=${this.attachmentsMode}
          .caption=${attachmentsTitle}
          @wm-event-changed=${this.eventAttachmentMode}
        ></wm-button-switch>`;
      },
      cancel: (hide?: boolean) => {
        const disabled = !(
          this.branchQuoteSupport &&
          !this.isReadonly &&
          this.branchQuoteSupport.status != BranchQuoteSupportStatus.Draft &&
          this.branchQuoteSupport.type !== BranchQuoteSupportItemType.QuoteReview &&
          //   !claims.isAgent &&
          !flagInSet(
            this.branchQuoteSupport.status,
            // eslint-disable-next-line no-bitwise
            BranchQuoteSupportStatus.Cancelled | BranchQuoteSupportStatus.Resolved
          )
        );
        return disabled && hide
          ? html``
          : html` <webmodule-button size="small" variant="default" ?disabled=${disabled} @click=${this.eventCancel}>
              ${LangBranchQuoteConversation.cancelTicket()}
            </webmodule-button>`;
      },
      close: () => {
        return html` <webmodule-button size="small" variant="primary" @click=${this.eventClose}>
          <webmodule-icon slot="prefix" library="fa" name="fas-circle-left"></webmodule-icon>
          ${LangBranchQuoteConversation.backToQuoteSupportRequest()}
        </webmodule-button>`;
      },
      postToSupplier: (hide?: boolean) => {
        const disabled = !(
          this.branchQuoteSupport &&
          !this.isReadonly &&
          this.branchQuoteSupport.type !== BranchQuoteSupportItemType.QuoteReview &&
          //   !claims.isAgent &&
          flagInSet(this.branchQuoteSupport.status, BranchQuoteSupportStatus.Draft)
        );

        return hide && disabled
          ? html``
          : html` <webmodule-button size="small" variant="primary" ?disabled=${disabled} @click=${this.eventActivate}>
              ${LangBranchQuoteConversation.issueTicket()}
            </webmodule-button>`;
      },
      reactivate: (hide?: boolean) => {
        const enabled =
          this.branchQuoteSupport &&
          !this.readonly &&
          this.canReactivate &&
          this.branchQuoteSupport.type !== BranchQuoteSupportItemType.QuoteReview;

        return hide && !enabled
          ? html``
          : html` <webmodule-button size="small" variant="primary" ?disabled=${!enabled} @click=${this.eventReactivate}>
              ${LangBranchQuoteConversation.reactivateTicket()}
            </webmodule-button>`;
      },
      resolve: (hide?: boolean) => {
        const disabled = !(
          this.branchQuoteSupport &&
          !this.isReadonly &&
          this.branchQuoteSupport.status != BranchQuoteSupportStatus.Draft &&
          this.branchQuoteSupport.type !== BranchQuoteSupportItemType.QuoteReview &&
          !flagInSet(
            this.branchQuoteSupport.status,
            // eslint-disable-next-line no-bitwise
            BranchQuoteSupportStatus.Cancelled | BranchQuoteSupportStatus.Resolved
          )
        );
        return hide && disabled
          ? html``
          : html` <webmodule-button size="small" variant="primary" ?disabled=${disabled} @click=${this.eventResolve}>
              ${LangBranchQuoteConversation.resolveTicket()}
            </webmodule-button>`;
      }
    };

    return result;
  }

  buttonsTemplates(): TemplateResult[] {
    const result: TemplateResult[] = [];
    if (!this.actions) this.actions = this.actionsTemplate();
    if (!this.branchQuoteSupport) return result;
    if (this.showNewNoteEditor) return result;

    result.push(this.actions.attachmentsOnlySwitch());
    result.push(this.actions.close());
    result.push(this.actions.cancel(true));
    result.push(this.actions.resolve(true));
    result.push(this.actions.reactivate(true));
    return result;
  }

  protected notifyCreateEntryEditor() {
    if (!this._firstUpdated) return;
    if (this.useExternalEditing)
      this.dispatchCustom('new-entry', {
        enabled: this.createEntryEditor !== undefined,
        editor: this.createEntryEditor
      });
  }

  protected notifyEditEntryEditor() {
    if (!this._firstUpdated) return;
    if (this.useExternalEditing)
      this.dispatchCustom('edit-entry', {
        enabled: this.editEntryEditor !== undefined,
        entry: this.editEntry,
        editor: this.editEntryEditor
      });
  }

  protected async showDraftSavedMessage() {
    //Not needed as per discussion with Shane
    //I suggest we use the normal toast notification but shane thinks it is to much
    //He'll talk to alexey as he wants to change the autosave message toast notification
  }

  protected eventUpdateEntry = async (e: CustomEvent<ConversationEntryUpdateEvent>) => {
    e.stopImmediatePropagation();
    const result = this.dispatchCustom('conversation-saved', {
      updateEvent: e.detail,
      showMessage: true,
      completionPromise: Promise.resolve(true)
    });
    await result.completionPromise;
    if (result.showMessage) await this.showDraftSavedMessage();
    this.requestUpdate(); // to refresh this conversation thread
  };

  protected eventPostEntry = async (e: CustomEvent<ConversationEntryUpdateEvent>) => {
    e.stopImmediatePropagation();
    if (this.hasChanged) await this.save();
    this.activateEditors();
    this.dispatchCustom('conversation-saved', {
      updateEvent: e.detail,
      showMessage: true
    });
  };

  protected eventCancelEntry = (e: CustomEvent<ConversationEntryUpdateEvent>) => {
    e.stopImmediatePropagation();
    this.activateEditors();
  };

  protected editConversationEntry(entry: ViewConversationEntry) {
    this.showNewNoteEditor = false;
    this.setEditEntryEditor(
      html` <wmview-conversation-entry-editor
        .conversationEntry=${entry}
        .showHeader=${false}
        id="editNoteEditor"
        .allowCancel=${!this.useExternalEditing || !entry.draft}
        @wm-event-update=${this.eventUpdateEntry}
        @wm-event-delete=${this.eventDeleteEntry}
        @wm-event-post=${this.eventPostEntry}
        @wm-event-cancel=${this.eventCancelEntry}
      ></wmview-conversation-entry-editor>`,
      entry
    );
  }

  protected activateEditors() {
    this.setEditEntryEditor(undefined, undefined);
    this.showNewNoteEditor = false;
    if (!this._resultsLoaded) return;
    if (this.useExternalEditing && this.alwaysShowNewNoteEditor) {
      this.ActivateExternalEditor();
      if (this.editEntryEditor != undefined) {
        this.notifyEditEntryEditor();
      } else if (this.createEntryEditor != undefined) {
        this.notifyCreateEntryEditor();
      }
    } else {
      if (this.alwaysShowNewNoteEditor) this.showNewNoteEditor = true;
    }
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    this._firstUpdated = true;

    if (this.useExternalEditing && this.alwaysShowNewNoteEditor) {
      this.activateEditors();
    }

    if (this.useExternalEditing) this.requestUpdate();
  }

  protected eventAttachmentMode = async (e: CustomEvent<{ checked: boolean }>) => {
    this.attachmentsMode = e.detail.checked;
  };

  protected eventAddNote = async (_e: Event) => {
    this.showNewNoteEditor = true;
  };

  protected eventSubjectChanged = (e: WebmoduleChangeEvent | WebmoduleInputEvent) => {
    this.subjectEditorValue = (e.target as HTMLInputElement).value;
  };

  private async changeState(status: BranchQuoteSupportStatus) {
    const validations: string[] = [];
    const result = this.dispatchCustom('changing', {
      branchQuoteSupport: this.branchQuoteSupport,
      status: status,
      allowChange: Promise.resolve(true),
      validations: validations
    });

    const canChange = await result.allowChange;
    if (result.validations.length > 0) {
      await showValidations(result.validations, () => LangBranchQuoteConversation.validation());
      return;
    }

    if (!canChange) return;

    await lockUIandExecute(async () => {
      if (!this.branchQuoteSupport) return;
      const result = await getApiFactory()
        .franchisee()
        .updateBranchQuoteSupport({
          quoteItemLink: this.showQuoteItemLink ? this.getQuoteItemLinks() : null,
          quoteItemIdToDetach: null,
          masterDocumentConversationId: null,
          branchQuoteSupportId: this.branchQuoteSupport.id,
          branchQuoteId: null,
          status: status,
          subject: this.showSubject ? (this.subjectEditorValue ?? null) : this.branchQuoteSupport.subject,
          conversationEntries: []
        });
      if (result) {
        fireQuickSuccessToast(LangBranchQuoteConversation.changesSaved());

        this.branchQuoteSupport = result?.branchQuoteSupport;
        this.subjectEditorValue = this.branchQuoteSupport?.subject;
        this.dispatchCustom('changed', {
          branchQuoteSupport: this.branchQuoteSupport
        });
      }
    });
  }

  private async updateBranchQuoteSupport(subject: string) {
    await lockUIandExecute(async () => {
      if (!this.branchQuoteSupport) return;
      const result = await getApiFactory()
        .franchisee()
        .updateBranchQuoteSupport({
          quoteItemLink: this.showQuoteItemLink ? this.getQuoteItemLinks() : null,
          masterDocumentConversationId: null,
          quoteItemIdToDetach: null,
          branchQuoteSupportId: this.branchQuoteSupport.id,
          branchQuoteId: null,
          status: null,
          subject: subject,
          conversationEntries: []
        });
      if (result) {
        fireQuickSuccessToast(LangBranchQuoteConversation.changesSaved());
        this.branchQuoteSupport = result?.branchQuoteSupport;
        this.subjectEditorValue = this.branchQuoteSupport?.subject;
        this.dispatchCustom('changed', {
          branchQuoteSupport: this.branchQuoteSupport
        });
      }

      // eslint-disable-next-line consistent-return
      return result !== null;
    });
  }
}
